import * as React from 'react';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../ui/form';
import { cn } from '../../lib/utils';
import type { Control, FieldPath } from 'react-hook-form';

export type FormRadioGroupOption = {
  value: string;
  label: string;
};

export type FormRadioGroupProps<T extends object> = Readonly<{
  control: Control<T>;
  name: FieldPath<T>;
  options: FormRadioGroupOption[];
  label?: string;
  description?: string;
  disabled?: boolean;
  className?: string;
  optional?: boolean;
  rowOnLg?: boolean; // If true, display as row on lg screens, else column
}>;

export function FormRadioGroup<T extends object>(
  props: FormRadioGroupProps<T>,
) {
  const {
    name,
    control,
    options,
    label,
    description,
    disabled,
    className,
    optional,
    rowOnLg = true,
  } = props;
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          {label && <FormLabel optional={optional}>{label}</FormLabel>}
          <FormControl>
            <RadioGroup
              value={field.value}
              onValueChange={field.onChange}
              disabled={disabled}
              className={cn(
                'flex flex-col gap-2',
                rowOnLg ? 'lg:flex-row lg:gap-10' : '',
              )}
            >
              {options.map((option) => (
                <label
                  key={option.value}
                  htmlFor={`${name}-${option.value}`}
                  className="flex min-w-0 cursor-pointer items-center gap-2"
                >
                  <RadioGroupItem
                    value={option.value}
                    disabled={disabled}
                    id={`${name}-${option.value}`}
                    className={cn(
                      'h-6 w-6 border-2 border-input data-[state=checked]:border-primary',
                      'bg-background',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                      'disabled:cursor-not-allowed disabled:opacity-50',
                    )}
                  />
                  <span className="text-s whitespace-nowrap">
                    {option.label}
                  </span>
                </label>
              ))}
            </RadioGroup>
          </FormControl>
          {description && (
            <div className="mt-1 text-xs text-muted-foreground">
              {description}
            </div>
          )}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
